import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import DateFilter from "../../../components/dateFilter";
import { primaryColor } from "../../../theme";
import { formatErrorDate } from "../../utilities/date.util";
import { SortableTableCell } from "../SkillMappings/SkillDetails";
import { modifiedResult } from "./utils";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import ProcessLogContent from "./ProcessLogContent";
import { getRequestHeaders } from "../../utilities/auth/request";
import { useDateFilter } from "../../../components/useDateFilter";
const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));
const ProcessLog = () => {
  const [sortOrder, setSortOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("CREATED_DT");
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = React.useState(false);
  const [event, setEvent] = React.useState("");
  const [eventSource, setEventSource] = React.useState("");
  const [apiData, setApiData] = useState([]);
  const navigate = useNavigate();
  const {
    selected,
    setSelected,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  } = useDateFilter();
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  const handleOpen = (event, eventType) => {
    setOpen(true);
    setEvent(event);
    setEventSource(eventType);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const { isPending, isError, data, error } = useQuery({
    queryKey: ["fetchProcessLog", startDate, endDate],
    queryFn: async () => {
      const body = {
        fromDate: startDate,
        toDate: endDate,
      };
      const request = getRequestHeaders("post", body);
      const response = await fetch(`/ui/fetchProcessLog`, request);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      setApiData(data);
      return modifiedResult(data);
    },
  });

  if (isPending) {
    return <span>Loading...</span>;
  }

  if (isError) {
    return <span>Error: {error.message}</span>;
  }

  const handleSortClick = (property) => {
    const isAsc = orderBy === property && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  const sortedData = () =>
    data
      ? data.sort((a, b) => {
          const order = sortOrder === "asc" ? 1 : -1;
          let aValue =
            orderBy === "CREATED_DT" ? new Date(a[orderBy]) : a[orderBy];
          let bValue =
            orderBy === "CREATED_DT" ? new Date(b[orderBy]) : b[orderBy];
          if (
            (aValue === null || aValue === undefined) &&
            (bValue === null || bValue === undefined)
          ) {
            return 1;
          }
          // Handle null or undefined values
          if (aValue === null || aValue === undefined) {
            return order;
          }
          if (bValue === null || bValue === undefined) {
            return -order;
          }

          // Normal string comparison
          if (aValue < bValue) {
            return -1 * order;
          }
          if (aValue > bValue) {
            return 1 * order;
          }
          return 0;
        })
      : [];

  const filteredData = () => {
    if (searchTerm) {
      return sortedData().filter((row) => {
        return Object.keys(row).some((key) =>
          row[key].toString().toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    } else {
      return sortedData();
    }
  };
  const getModalTitle = (eventSource) => {
    switch (eventSource) {
      case "USER_LOCKED":
      case "USER_UNLOCKED":
        return "Lock and Unlock Report -- Process Run ";
      case "SKILL_ASSIGNED":
      case "SKILL_UNASSIGNED":
        return "Skill Assignment Report  -- Process Run ";
      case "ERRORS_COUNT":
        return "Errors & Exceptions Report -- Process Run";
      default:
        return "Modal Title";
    }
  };
  const handleErrorsClick = () => {
    navigate(`/errors-exceptions`);
  };
  const filteredDataList = filteredData();
  return (
    <>
      <Typography
        variant="h4"
        component={"div"}
        sx={{
          marginBottom: "1rem",
        }}
      >
        Process Log
      </Typography>
      <Box
        sx={{ backgroundColor: "white" }}
        width={"100%"}
        height={"calc(100vh - 128px)"}
        overflow={"auto"}
      >
        <Box display={"flex"} width={"65%"} justifyContent={"space-between"}>
          <Typography
            variant="h6"
            component={"div"}
            sx={{
              p: "1rem",
              fontWeight: "400",
            }}
          >
            <DateFilter
              selected={selected}
              setSelected={setSelected}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
            />
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" component={"div"}>
              Filter Content
            </Typography>
            <TextField
              size="small"
              margin="normal"
              fullWidth
              sx={{
                padding: 0,
                margin: 0,
                paddingTop: "5px",
                minWidth: 200,
              }}
              id="search"
              name="search"
              autoComplete="off"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon style={{ color: primaryColor }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        <TableContainer>
          <Box
            sx={{
              maxHeight: "calc(100vh - 230px)",
              overflow: "auto",
              ml: 2,
              mr: 2,
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <SortableTableCell
                    property="CREATED_DT"
                    orderBy={orderBy}
                    sortOrder={sortOrder}
                    onClick={handleSortClick}
                    active={orderBy === "CREATED_DT"}
                  >
                    DATE
                  </SortableTableCell>
                  <SortableTableCell
                    property="EVENT_SOURCE"
                    orderBy={orderBy}
                    sortOrder={sortOrder}
                    onClick={handleSortClick}
                    active={orderBy === "EVENT_SOURCE"}
                  >
                    PROCESS RUN ID
                  </SortableTableCell>
                  <SortableTableCell
                    property="USERS_LOCKED"
                    orderBy={orderBy}
                    sortOrder={sortOrder}
                    onClick={handleSortClick}
                    active={orderBy === "USERS_LOCKED"}
                  >
                    USERS LOCKED
                  </SortableTableCell>
                  <SortableTableCell
                    property="USERS_UNLOCKED"
                    orderBy={orderBy}
                    sortOrder={sortOrder}
                    onClick={handleSortClick}
                    active={orderBy === "USERS_UNLOCKED"}
                  >
                    USERS UNLOCKED
                  </SortableTableCell>
                  <SortableTableCell
                    property="SKILLS_ASSIGNED"
                    orderBy={orderBy}
                    sortOrder={sortOrder}
                    onClick={handleSortClick}
                    active={orderBy === "SKILLS_ASSIGNED"}
                  >
                    SKILLS ASSIGNED
                  </SortableTableCell>
                  <SortableTableCell
                    property="SKILLS_UNASSIGNED"
                    orderBy={orderBy}
                    sortOrder={sortOrder}
                    onClick={handleSortClick}
                    active={orderBy === "SKILLS_UNASSIGNED"}
                  >
                    SKILLS UNASSIGNED
                  </SortableTableCell>
                  <SortableTableCell
                    property="ERRORS_COUNT"
                    orderBy={orderBy}
                    sortOrder={sortOrder}
                    onClick={handleSortClick}
                    active={orderBy === "ERRORS_COUNT"}
                  >
                    ERRORS & EXCEPTIONS
                  </SortableTableCell>
                </TableRow>
              </TableHead>
              <TableBody key={filteredDataList.length}>
                {filteredDataList?.map((row) => (
                  <TableRow>
                    <TableCell>{row.CREATED_DT}</TableCell>
                    <TableCell>{row.EVENT_SOURCE}</TableCell>
                    <TableCell>
                      <Link
                        onClick={() => {
                          handleOpen(row, "USER_LOCKED");
                        }}
                      >
                        {row.USERS_LOCKED}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        onClick={() => {
                          handleOpen(row, "USER_UNLOCKED");
                        }}
                      >
                        {row.USERS_UNLOCKED}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        onClick={() => {
                          handleOpen(row, "SKILL_ASSIGNED");
                        }}
                      >
                        {row.SKILLS_ASSIGNED}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        onClick={() => {
                          handleOpen(row, "SKILL_UNASSIGNED");
                        }}
                      >
                        {row.SKILLS_UNASSIGNED}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        onClick={() => {
                          handleOpen(row, "ERRORS_COUNT");
                        }}
                      >
                        {row.ERRORS_COUNT}
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </TableContainer>
        <BootstrapDialog
          open={open}
          maxWidth="lg"
          fullWidth={true}
          onClose={handleClose}
          PaperProps={{ style: { minHeight: "70vh" } }}
        >
          <DialogTitle
            sx={{
              padding: "1rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box>
              {getModalTitle(eventSource)} {event.EVENT_SOURCE}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: "700" }}>
              {formatErrorDate(event.CREATED_DT)}
            </Typography>
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,

              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <ProcessLogContent
              eventSource={eventSource}
              event={event}
              data={apiData}
            />
          </DialogContent>
          <DialogActions
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {eventSource === "ERRORS_COUNT" && (
              <Button
                sx={{
                  mt: 2,
                  mb: 2,
                }}
                variant="contained"
                onClick={handleErrorsClick}
              >
                View All Errors & Exceptions
              </Button>
            )}
          </DialogActions>
        </BootstrapDialog>
      </Box>
    </>
  );
};

export default ProcessLog;
